// displayMode — per-user display preference (dark / high-contrast).
//
// Bug 15 background: pre-fix the mode lived in a single localStorage key
// ('dc_display_mode'), shared across every account on the same browser.
// Player A toggling Sun Mode persisted globally; Player B logging in
// inherited it. Now each user gets their own keyed entry, plus a separate
// 'dc_active_user_id' pointer so the pre-paint hook in main.jsx (which
// runs before AuthContext hydrates) knows whose mode to apply.
//
// On sign-out we wipe the active pointer + revert <html data-dc-mode>
// to dark, but keep each user's per-user key so re-signing-in restores
// their preference instead of forgetting it.

const ACTIVE_KEY = 'dc_active_user_id'
const LEGACY_KEY = 'dc_display_mode'
const MODE_KEY = (userId) => `dc_display_mode_${userId}`
const VALID = new Set(['dark', 'high-contrast'])
const DEFAULT_MODE = 'dark'

const safeRead = (key) => {
  try { return localStorage.getItem(key) } catch { return null }
}
const safeWrite = (key, value) => {
  try { localStorage.setItem(key, value) } catch { /* ignore */ }
}
const safeRemove = (key) => {
  try { localStorage.removeItem(key) } catch { /* ignore */ }
}

export function getActiveUserId() {
  return safeRead(ACTIVE_KEY)
}

export function getStoredMode(userId) {
  if (!userId) return DEFAULT_MODE
  const v = safeRead(MODE_KEY(userId))
  return VALID.has(v) ? v : DEFAULT_MODE
}

export function setStoredMode(userId, mode) {
  if (!userId || !VALID.has(mode)) return
  safeWrite(MODE_KEY(userId), mode)
  safeWrite(ACTIVE_KEY, userId)
}

export function setActiveUser(userId) {
  if (!userId) return
  safeWrite(ACTIVE_KEY, userId)
}

export function applyMode(mode) {
  if (typeof document === 'undefined') return
  try {
    const resolved = VALID.has(mode) ? mode : DEFAULT_MODE
    document.documentElement.dataset.dcMode = resolved
    document.documentElement.classList.toggle('flare', resolved === 'high-contrast')
  } catch { /* ignore */ }
}

// Called on logout. Drops the active-user pointer so the next page load
// (which runs the main.jsx pre-paint hook before any auth hydrates) sees
// no active user and falls back to dark. Per-user preferences stay so
// re-login restores them.
export function clearActiveOnLogout() {
  safeRemove(ACTIVE_KEY)
  applyMode(DEFAULT_MODE)
}

// One-time migration. Pre-Bug-15 the mode lived in a single global key.
// If we still see it with no active-user pointer (= nobody has signed in
// under the new scheme yet), drop it — it has no owner and would
// otherwise leak the previous tester's preference to the next signer-in.
export function purgeLegacyKeyIfOrphaned() {
  if (!safeRead(ACTIVE_KEY) && safeRead(LEGACY_KEY)) {
    safeRemove(LEGACY_KEY)
  }
}
